<template>
  <div class="w-full h-full flex justify-center items-center pending">
    <div
        v-if="returnedStatus"
        class="flex h-8 w-full items-center justify-start rounded-2xl border border-input bg-background px-3 py-2 text-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:truncate"
        :class="{
              '!bg-[#FFFFFF] border-red-500 text-red-500': returnedStatus == 'returned',
              '!bg-[#FFFFFF] border-[#93831E] text-[#93831E]': returnedStatus == 'partialReturn',
               }">
      {{ t(`orderStatus.${returnedStatus}`) }}
    </div>
    <div
        v-if="isDirectDelivered && !returnedStatus"
        class="flex h-8 w-full items-center justify-start rounded-2xl border border-input bg-background px-3 py-2 text-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:truncate !bg-[#D1F1BB]"
    >
      {{ t('orderStatus.delivered') }}
    </div>
    <Select v-if="!returnedStatus && !isDirectDelivered" v-model="value"
            :dir="ctx.locale == 'ar'? 'rtl': ''">
      <SelectTrigger class="w-full"
                     :class="{
                          'bg-[#FCC5C5]': value == 'unprocessed',
                          'bg-[#FFE072]': value == 'processing',
                          'bg-[#B8DAFC]': value == 'ready',
                          'bg-[#D0C5F4]': value == 'on_the_way',
                          'bg-[#D1F1BB]': value == 'delivered',
                     }">
        <SelectValue/>
      </SelectTrigger>
      <SelectContent class="">
        <SelectItem value="unprocessed">
          {{ t('orderStatus.unprocessed') }}
        </SelectItem>
        <SelectItem value="processing">
          {{ t('orderStatus.processing') }}
        </SelectItem>
        <SelectItem value="ready">
          {{ t('orderStatus.ready') }}
        </SelectItem>
        <SelectItem value="on_the_way">
          {{ t('orderStatus.onTheWay') }}
        </SelectItem>
        <SelectItem value="delivered">
          {{ t('orderStatus.delivered') }}
        </SelectItem>
      </SelectContent>
    </Select>
  </div>
</template>
<script lang="ts">
import {defineComponent, ref} from 'vue'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {useContext} from "@/plugins/context";
import {useI18n} from "vue-i18n";
import {i18nOrderMessages, Order} from "@/models/sales/Order";
import {ElMessage, ElMessageBox} from "element-plus";
import {useSettingsStore} from "@/stores/settingsStore";
import {useRouter} from "vue-router";

export default defineComponent({
  setup() {
    const ctx = useContext();
    const router = useRouter();
    const {t} = useI18n({
      messages: {
        en: {...i18nOrderMessages.en},
        ar: {...i18nOrderMessages.ar}
      }
    })

    const order = ref<Order | null>(null);
    const value = ref('')
    const previousValue = ref('')
    const isDirectDelivered = ref(false)
    const label = ref('')

    const returnedStatus = ref<string | null>(null)

    return {
      ctx,
      router,
      t,
      order,
      value,
      previousValue,
      label,
      returnedStatus,
      isDirectDelivered
    }
  },
  components: {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  },
  props: ['data'],
  mounted() {
    /* @ts-ignore */
    this.order = (this.params?.data || this.$props.data) as Order;
    if (!this.order) {
      return;
    }

    // check if order is returned
    if (this.order.orderItems.filter((oi) => oi.returnedQuantity > 0).length > 0) {
      this.returnedStatus = 'partialReturn'
    }

    if (this.order.orderItems.filter((oi) => oi.quantity == oi.returnedQuantity).length == this.order.orderItems.length) {
      this.returnedStatus = 'returned'
    }

    switch (this.order.orderStatus) {
      case 'unprocessed':
        this.value = 'unprocessed'
        this.label = this.t('orderStatus.unprocessed')
        break;
      case 'processing':
        this.value = 'processing'
        this.label = this.t('orderStatus.processing')
        break;
      case 'ready':
        this.value = 'ready'
        this.label = this.t('orderStatus.ready')
        break;
      case 'on_the_way':
        this.value = 'on_the_way'
        this.label = this.t('orderStatus.onTheWay')
        break;
      case 'delivered':
        this.value = 'delivered'
        this.label = this.t('orderStatus.delivered')
        break;
    }
    this.previousValue = this.value;
    this.isDirectDelivered = this.order.orderStatus == 'delivered' && this.order.orderType == 'direct'
  },
  watch: {
    value: {
      handler(newVal) {
        if (this.previousValue != newVal) {
          this.order.orderStatus = newVal;
          this.onChangeStatus(newVal)
        }
      }
    },
  },
  methods: {
    submit(pin: string = "") {
      if (pin) {
        this.$http
            .post<Order>(
                `${Order.ENDPOINT}/change-order-status/${this.order.id}`,
                this.order, {headers: {pin: pin}}
            )
            .then((resp) => {
              this.previousValue = this.value;

              /* eslint-disable vue/no-mutating-props */
              ElMessage({
                type: 'success',
                message: this.t('dialogs.msgUpdated')
              })
            })
            .catch((error) => {
              this.value = this.previousValue;
              ElMessage({
                type: 'error',
                message: this.t(error?.title)
              })
            })
        return;
      }

      this.$http
          .post<Order>(
              `${Order.ENDPOINT}/change-order-status/${this.order.id}`,
              this.order
          )
          .then((resp) => {
            this.previousValue = this.value;

            /* eslint-disable vue/no-mutating-props */
            ElMessage({
              type: 'success',
              message: this.t('dialogs.msgUpdated')
            })
          })
          .catch(() => {
            this.value = this.previousValue;
            ElMessage({
              type: 'error',
              message: 'error'
            })
          })
    },
    onChangeStatus(event: any) {
      // Check if this is a fulfillment order that should redirect to fulfillment page
      const isFulfillmentMode = useSettingsStore().getValue('order_fulfilling_module') == 'true'
      const isFulfillmentOrder = this.order?.orderType === 'delivery' || this.order?.orderType === 'pick_up'
      
      // Only redirect if trying to skip the fulfillment workflow:
      // - From 'unprocessed' to anything except 'processing'
      // - From 'processing' to anything except 'unprocessed'
      const isSkippingFulfillment = 
        (this.previousValue === 'unprocessed' && this.value !== 'processing') ||
        (this.previousValue === 'processing' && this.value !== 'unprocessed')
      
      if (isFulfillmentMode && isFulfillmentOrder && isSkippingFulfillment) {
        // Reset value and redirect to fulfillment page
        this.value = this.previousValue
        
        // Navigate to fulfillment page
        this.router.push(this.$Route.SALES_ORDERS_ORDER_FULFILL.replace(':id', this.order?.id as string))
        return
      }

      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        ElMessageBox.prompt(this.t('dialogs.confirmAction'), this.t('dialogs.warning'), {
          confirmButtonText: this.t('dialogs.confirm'),
          cancelButtonText: this.t('dialogs.cancel'),
          type: 'warning',
          inputType: 'password',
          inputPlaceholder: 'PIN Code'
        })
            .then(({value}) => {
              this.submit(value)
            })
            .catch(() => {
              console.log('cancelled')
              this.value = this.previousValue;
            })
        return;
      }

      ElMessageBox.confirm(this.t('dialogs.confirmAction'), this.t('dialogs.warning'), {
        confirmButtonText: this.t('dialogs.confirm'),
        cancelButtonText: this.t('dialogs.cancel'),
        type: 'warning'
      })
          .then(() => {
            this.submit()
          })
          .catch(() => {
            console.log('cancelled')
            this.value = this.previousValue;
          })
    }
  }
})
</script>
<style>
</style>
